import {
  communicationStatus,
  ConfirmationModal,
  Modal,
  SelectDropDown,
  Tooltip,
} from '@ui-kit'
import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '../../utils/agent.store'
import {
  correspondenceLogsPage,
  ENTITY_ID,
  NAMESPACES,
  STATUS_IDS,
  StatusConfig,
} from '../../utils/constants'
import {
  addToaster,
  CategoryStatusPayload,
  changeRecordStatusForEntity,
  EnrollmentStatusPayload,
  hideLoader,
  showLoader,
} from '@app-store'
import { v4 as uuid } from 'uuid'
import { toTitleCase } from '../../utils/helperFunctions'
import { STRINGS } from '../../utils/strings'

const CommunicationStatus = ({
  approved,
  tableType,
  recordId,
  statusReason,
  setRefetchKey,
  onChange,
}: {
  approved: string
  tableType: string
  recordId: string
  statusReason?: string
  setRefetchKey: React.Dispatch<React.SetStateAction<number>>
  onChange?: () => void
}) => {
  const statusKey = approved?.toUpperCase()
  const config = StatusConfig[statusKey] ?? {
    label: statusKey,
    bgColor: 'bg-gray-100',
    textColor: 'text-accent-900',
  }
  const label = toTitleCase(config?.label)
  return tableType !== correspondenceLogsPage.OUTBOUND_LOWERCASE &&
    (statusKey === communicationStatus?.VOID_UPPERCASE ||
      statusKey === communicationStatus.DISCARDED_UPPERCASE) ? (
    <VoidOrDiscardDropdown
      approved={approved}
      tableType={tableType}
      recordId={recordId}
      statusReason={statusReason}
      setRefetchKey={setRefetchKey}
      onChange={onChange}
    />
  ) : (
    <Tooltip
      content={statusReason}
      cssClassName="flex items-center justify-center"
    >
      <div className="flex justify-center w-full h-[1.75rem] items-center">
        <div
          className={`px-1 leading-[1.75rem] font-regular text-sm w-full h-[1.75rem] truncate rounded text-center ${config?.bgColor} ${config?.textColor}`}
        >
          {label}
        </div>
      </div>
    </Tooltip>
  )
}

const VoidOrDiscardDropdown = ({
  approved,
  tableType,
  recordId,
  setRefetchKey,
  onChange,
  statusReason,
}: {
  approved: string
  tableType: string
  recordId: string
  statusReason?: string
  setRefetchKey: React.Dispatch<React.SetStateAction<number>>
  onChange?: () => void
}) => {
  const userId = useAppSelector((state) => state?.authUser?.user?.id)
  const [modal, setModal] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const getDrodownLabel = () => {
    if (approved?.toUpperCase() === communicationStatus.DISCARDED_UPPERCASE) {
      return communicationStatus.DISCARDED
    }
    return communicationStatus.VOID
  }

  const handleStatusChange = async () => {
    try {
      dispatch(showLoader())
      const isWeb = tableType === correspondenceLogsPage.INBOUND_WEB_KEY
      const entityId = isWeb
        ? ENTITY_ID.HCP_PORTAL_ENTITY
        : ENTITY_ID.COMMUNICATION
      const statusId = isWeb ? STATUS_IDS.WEB_RECEIVED : STATUS_IDS.FAX_RECEIVED
      const payload = {
        meta_data: {
          entity_id: entityId,
          embedded: [
            isWeb ? 'enrollment_status_id' : 'category_status_id',
            'users_id',
          ],
        },
        data: {
          [isWeb ? 'enrollment_status_id' : 'category_status_id']: statusId,
          users_id: userId,
        },
      }
      await changeRecordStatusForEntity(
        payload as CategoryStatusPayload | EnrollmentStatusPayload,
        parseInt(recordId)
      )
      setRefetchKey((prev: number) => prev + 1)
      setModal(false)
      onChange && onChange()
      dispatch(
        addToaster({
          message: NAMESPACES.RECORD_STATUS_CHANGED_TO_RECEIVED_SUCCESSFULLY,
          type: 'success',
          id: uuid(),
        })
      )
    } catch {
      dispatch(
        addToaster({
          message: NAMESPACES.ERROR_UPDATING_RECORD_STATUS,
          type: 'error',
          id: uuid(),
        })
      )
    } finally {
      dispatch(hideLoader())
    }
  }

  const getConfirmationMessage = () => {
    const state = getDrodownLabel()
    if (state?.toUpperCase() === communicationStatus.DISCARDED_UPPERCASE) {
      return STRINGS.DISCARDED_STATUS_CHANGE_CONFIRMATION_MSG
    }
    return STRINGS.VOID_STATUS_CHANGE_CONFIRMATION_MSG
  }

  return (
    <Tooltip
      content={statusReason}
      position="bottom"
      className="tooltip-text-wrap"
    >
      <>
        <div className="flex items-center justify-center w-full h-[]">
          <div className="flex w-full font-light text-sm truncate block bg-accent-100 rounded text-center text-error-500">
            <SelectDropDown
              label={''}
              options={[
                {
                  label: communicationStatus.RECIEVED,
                  value: communicationStatus.RECIEVED,
                },
              ]}
              name={`status-${recordId}`}
              onChange={() => setModal(true)}
              placeholder={getDrodownLabel()}
              className="h-[28px] flex"
              isCrossable={false}
              height="h-[1rem]"
              isBordered={false}
              checkPosition={false}
            />
          </div>
        </div>
        {modal && (
          <Modal
            isOpen={modal}
            closeModal={() => setModal(false)}
            showCloseButton={true}
            width="w-[38.5rem]"
          >
            <ConfirmationModal
              message={getConfirmationMessage()}
              handleNo={() => setModal(false)}
              handleYes={() => {
                handleStatusChange()
              }}
            />
          </Modal>
        )}
      </>
    </Tooltip>
  )
}

export default CommunicationStatus
